import { useCallback, useEffect, useState } from 'react';
import { DocumentType } from '@/lib/constants/documentVersions';
import { useDocumentAcknowledgement } from '@/hooks/useDocumentAcknowledgement';

interface Options {
  /** The document type whose acknowledgement state gates this changelog. */
  documentType: DocumentType;
  /** The version date (YYYY-MM-DD) the changelog box describes. */
  versionAnchor: string;
  /** Hard cutoff date (YYYY-MM-DD) after which the box is hidden for everyone. */
  expiresAt: string;
}

/**
 * Visibility logic for the in-page "What changed recently" highlight box on
 * legal policy pages (Privacy, Cookie, ...).
 *
 * Three independent gates — the box is shown only if ALL are true:
 *
 *   1. Within the disclosure window (today <= expiresAt).
 *   2. The user has not already acknowledged this version (either via the top
 *      PolicyUpdateBanner or via the box's own dismiss button).
 *   3. The user is not brand-new — `lastAckedVersion === null` means no prior
 *      acknowledgement record exists, which `useDocumentAcknowledgement` treats
 *      as an implicit-accept-at-signup user. They never lived through the
 *      change, so flagging it would be confusing.
 *
 * Force-show override: if `window.location.hash === '#changelog-<versionAnchor>'`
 * (e.g. arriving from the top PolicyUpdateBanner's deep link), the box is
 * shown regardless of the gates so the anchor scroll target actually exists.
 *
 * `dismiss()` calls `acknowledge()` from useDocumentAcknowledgement, which
 * writes localStorage AND fires a `<documentType>_acknowledged` row to the
 * `user_consents` audit trail for authenticated users.
 */
export const useChangelogVisibility = ({
  documentType,
  versionAnchor,
  expiresAt,
}: Options) => {
  const { lastAckedVersion, acknowledge } = useDocumentAcknowledgement(documentType);

  const [forceShow, setForceShow] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.location.hash === `#changelog-${versionAnchor}`;
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onHash = () => {
      setForceShow(window.location.hash === `#changelog-${versionAnchor}`);
    };
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, [versionAnchor]);

  const withinWindow = (() => {
    try {
      const now = Date.now();
      const cutoff = new Date(`${expiresAt}T23:59:59Z`).getTime();
      if (Number.isNaN(cutoff)) return true;
      return now <= cutoff;
    } catch {
      return true;
    }
  })();

  const alreadyAcknowledged =
    lastAckedVersion !== null && lastAckedVersion >= versionAnchor;

  const isBrandNewUser = lastAckedVersion === null;

  const visible =
    forceShow || (withinWindow && !alreadyAcknowledged && !isBrandNewUser);

  const dismiss = useCallback(async () => {
    await acknowledge();
    setForceShow(false);
  }, [acknowledge]);

  return { visible, dismiss };
};
